import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ProjectFailedToLoad } from "@/components/projects/ProjectErrors";
import {
    useProjectPrebuilds,
    useCancelPrebuild,
    useCreatePrebuild,
    useDeletePrebuild,
} from "@/queries/prebuild-queries";
import { useEnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import { useMemo, useState, useCallback, type FC } from "react";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { useParams } from "react-router-dom";
import { useProject } from "@/queries/project-queries";
import { useTriggerPrebuild } from "@/hooks/use-trigger-prebuild.tsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/podkit/tables/Table";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import IconCircleX from "central-icons/IconCircleX";
import IconCircleCheck from "central-icons/IconCircleCheck";
import IconCircleMinus from "central-icons/IconCircleMinus";
import { IconLoader } from "central-icons/IconLoader";
import { cn } from "@/components/podkit/lib/cn";
import { Banner } from "@/components/flexkit/Banner";
import { Timestamp } from "@bufbuild/protobuf";
import { durationBetweenDates, formatDateWithTime } from "@/format/time";
import { PrebuildPhase, PrebuildMetadata_PrebuildTrigger } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { getPrebuildScheduleMessage } from "@/utils/prebuild-schedule";
import { isInProgressPrebuildPhase } from "@/utils/prebuilds";
import { PrebuildLogsModal } from "@/components/projects/PrebuildLogsModal";
import { Tooltip } from "@/components/Tooltip";
import { SubjectDisplay } from "@/components/SubjectDisplay";

export const ProjectPrebuildsPage: FC = () => {
    const { projectId } = useParams();
    const { data, isPending, error } = useProjectPrebuilds(projectId);
    const { data: project } = useProject(projectId);
    const { environmentClasses, isLoading: isLoadingClassMap } = useEnvironmentClassMap();
    const createPrebuild = useCreatePrebuild();
    const cancelPrebuild = useCancelPrebuild();
    const deletePrebuild = useDeletePrebuild();
    const { trigger: triggerPrebuild, isSubmitting } = useTriggerPrebuild(
        projectId,
        project?.prebuildConfiguration?.environmentClassIds ?? [],
    );
    const [selectedPrebuildId, setSelectedPrebuildId] = useState<string | null>(null);

    const sortedPrebuilds = useMemo(() => {
        const list = data?.prebuilds ?? [];
        return [...list].sort((a, b) => {
            const aTime = a?.metadata?.createdAt ? new Timestamp(a.metadata.createdAt).toDate().getTime() : 0;
            const bTime = b?.metadata?.createdAt ? new Timestamp(b.metadata.createdAt).toDate().getTime() : 0;
            return bTime - aTime;
        });
    }, [data?.prebuilds]);

    const selectedPrebuild = useMemo(() => {
        if (!selectedPrebuildId) return null;
        return sortedPrebuilds.find((pb) => pb.id === selectedPrebuildId) || null;
    }, [selectedPrebuildId, sortedPrebuilds]);

    const handleRerun = useCallback(async () => {
        if (!projectId) {
            toast({ title: "Cannot rerun prebuild", description: "Project ID not found" });
            return;
        }

        if (!selectedPrebuild) {
            toast({ title: "Cannot rerun prebuild", description: "Prebuild not found" });
            return;
        }

        const environmentClassId = selectedPrebuild.metadata?.environmentClassId;
        if (!environmentClassId) {
            toast({ title: "Cannot rerun prebuild", description: "Environment class not found" });
            return;
        }

        try {
            const newPrebuild = await createPrebuild.mutateAsync({
                projectId,
                environmentClassId,
            });

            toast({ title: "Prebuild started successfully" });

            // Open the new prebuild in the modal
            if (newPrebuild.id) {
                setSelectedPrebuildId(newPrebuild.id);
            }
        } catch (err) {
            toast({
                title: "Failed to start prebuild",
                description: formatError(err),
            });
        }
    }, [projectId, selectedPrebuild, createPrebuild]);

    const handleCancel = useCallback(async () => {
        if (!selectedPrebuildId) {
            toast({ title: "Cannot cancel prebuild", description: "Prebuild ID not found" });
            return;
        }

        try {
            await cancelPrebuild.mutateAsync(selectedPrebuildId);
            toast({ title: "Prebuild cancelled successfully" });
        } catch (err) {
            toast({
                title: "Failed to cancel prebuild",
                description: formatError(err),
            });
        }
    }, [selectedPrebuildId, cancelPrebuild]);

    // Find the active prebuild (most recent successful one per environment class)
    // Only show active badges when prebuilds are enabled on the project
    const activePrebuildIds = useMemo(() => {
        const activeIds = new Set<string>();

        // Don't show active badges if prebuilds aren't enabled
        if (!project?.prebuildConfiguration?.enabled) {
            return activeIds;
        }

        const classToPrebuild = new Map<string, (typeof sortedPrebuilds)[0]>();

        for (const pb of sortedPrebuilds) {
            const classId = pb.metadata?.environmentClassId;
            if (!classId) continue;

            // Check if this is a completed prebuild that's still active (not cancelled/deleted)
            const isCompleted = pb.status?.phase === PrebuildPhase.COMPLETED;
            const desiredPhaseCompleted = pb.spec?.desiredPhase === PrebuildPhase.COMPLETED;

            if (isCompleted && desiredPhaseCompleted) {
                // Only set if we haven't seen a prebuild for this class yet (since list is sorted by date desc)
                if (!classToPrebuild.has(classId)) {
                    classToPrebuild.set(classId, pb);
                    activeIds.add(pb.id);
                }
            }
        }

        return activeIds;
    }, [sortedPrebuilds, project?.prebuildConfiguration?.enabled]);

    const runningCount = useMemo(() => {
        return (sortedPrebuilds || []).filter((pb) => isInProgressPrebuildPhase(pb?.status?.phase)).length;
    }, [sortedPrebuilds]);

    let content: React.ReactNode | null = null;
    if (error || !projectId) {
        content = <ProjectFailedToLoad />;
    } else if (isPending || isLoadingClassMap) {
        content = (
            <div className="rounded-lg border border-border-base bg-surface-pure">
                <SkeletonBlock ready={false} className="h-[200px] w-full" />
            </div>
        );
    } else if ((sortedPrebuilds?.length || 0) === 0) {
        const prebuildEnabled = project?.prebuildConfiguration?.enabled || false;
        const scheduleMessage = getPrebuildScheduleMessage(project?.prebuildConfiguration);

        content = (
            <div className="mt-8">
                <div
                    className="flex flex-col items-center gap-4 rounded-lg border border-border-light p-6"
                    data-testid="prebuilds-empty-state"
                >
                    <div className="flex flex-col items-center gap-1 text-center">
                        <p className="text-base font-medium text-content-primary">No prebuilds have run yet.</p>
                        <p className="text-sm text-content-secondary">{scheduleMessage}</p>
                    </div>
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={triggerPrebuild}
                        loading={isSubmitting}
                        disabled={!prebuildEnabled}
                        data-testid="run-prebuild-now-button"
                        data-tracking-id="run-prebuild-now-empty-state"
                    >
                        Run Prebuild Now
                    </Button>
                </div>
            </div>
        );
    } else {
        content = (
            <div className="flex flex-col">
                <Table>
                    <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                        <TableRow className="h-8 text-base text-content-strong">
                            <TableHead className="pl-3">Date & Time</TableHead>
                            <TableHead className="pl-3">Class</TableHead>
                            <TableHead className="pl-3">Trigger</TableHead>
                            <TableHead className="pl-3">Ran as</TableHead>
                            <TableHead className="pl-3">Status & Duration</TableHead>
                            <TableHead className="w-[80px]" />
                        </TableRow>
                    </TableHeader>
                    <TableBody className="border-t border-border-light text-base" data-testid="prebuilds-table">
                        {sortedPrebuilds.map((pb) => {
                            const createdAt = pb.metadata?.createdAt
                                ? new Timestamp(pb.metadata.createdAt).toDate()
                                : undefined;
                            const completedAt = pb.status?.completionTime
                                ? new Timestamp(pb.status.completionTime).toDate()
                                : undefined;
                            const clazzEntry = pb.metadata?.environmentClassId
                                ? environmentClasses[pb.metadata.environmentClassId]
                                : undefined;

                            const triggerText = triggerToText(pb.metadata?.triggeredBy);
                            const status = statusInfo(pb.status?.phase, pb.spec?.desiredPhase);
                            const dur =
                                createdAt && completedAt
                                    ? durationBetweenDates(createdAt, completedAt)
                                    : createdAt && status.icon === "spinner"
                                      ? durationBetweenDates(createdAt, new Date())
                                      : "";
                            const isActive = activePrebuildIds.has(pb.id);

                            return (
                                <TableRow key={pb.id} className="h-16 border-border-light">
                                    <TableCell>
                                        <Text className="text-base">
                                            {createdAt ? formatDateWithTime(createdAt) : ""}
                                        </Text>
                                    </TableCell>
                                    <TableCell>
                                        {clazzEntry ? (
                                            <Text className="text-base">
                                                <span className="font-medium text-content-primary">
                                                    {clazzEntry.clazz.displayName}
                                                </span>{" "}
                                                <span className="text-content-tertiary">{clazzEntry.runner.name}</span>
                                            </Text>
                                        ) : (
                                            <Text className="text-base">—</Text>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Text className="text-base">{triggerText}</Text>
                                    </TableCell>
                                    <TableCell>
                                        {pb.metadata?.executor ? (
                                            <SubjectDisplay subject={pb.metadata.executor} />
                                        ) : pb.metadata?.creator ? (
                                            <SubjectDisplay subject={pb.metadata.creator} />
                                        ) : (
                                            <Text className="text-base text-content-tertiary">—</Text>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            {status.icon === "spinner" && (
                                                <IconLoader size={16} className="animate-spin text-content-secondary" />
                                            )}
                                            {status.icon === "success" && (
                                                <IconCircleCheck size={16} className="text-content-success" />
                                            )}
                                            {status.icon === "error" && (
                                                <IconCircleX size={16} className="text-content-destructive" />
                                            )}
                                            {status.icon === "cancelled" && (
                                                <IconCircleMinus size={16} className="text-content-secondary" />
                                            )}
                                            <Text
                                                className={cn(
                                                    "text-base",
                                                    status.icon === "spinner"
                                                        ? "text-content-strong"
                                                        : "text-content-primary",
                                                )}
                                            >
                                                {status.icon === "spinner" ? status.label + "..." : dur}
                                            </Text>
                                            {isActive && (
                                                <Tooltip content="This prebuild is used when creating new environments">
                                                    <span className="inline-flex items-center rounded-md bg-content-success/10 px-2 py-0.5 text-xs font-medium text-content-success">
                                                        Active
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-2">
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                data-tracking-id="open-prebuild-logs"
                                                onClick={() => setSelectedPrebuildId(pb.id)}
                                            >
                                                Logs
                                            </Button>
                                            <DropdownActions triggerTestId="prebuild-actions-menu">
                                                <DropdownMenuItem
                                                    data-tracking-id="copy-prebuild-id"
                                                    onClick={async () => {
                                                        if (pb.id) {
                                                            try {
                                                                await navigator.clipboard.writeText(pb.id);
                                                                toast({
                                                                    title: "Prebuild ID copied to clipboard",
                                                                    description: pb.id,
                                                                });
                                                            } catch (error) {
                                                                toast({
                                                                    title: "Failed to copy ID to clipboard",
                                                                    description: formatError(error),
                                                                });
                                                            }
                                                        }
                                                    }}
                                                >
                                                    Copy ID
                                                </DropdownMenuItem>
                                                {status.icon === "spinner" && (
                                                    <DropdownMenuItem
                                                        data-tracking-id="cancel-prebuild"
                                                        onClick={() => {
                                                            if (pb.id) {
                                                                cancelPrebuild.mutate(pb.id);
                                                            }
                                                        }}
                                                    >
                                                        Cancel
                                                    </DropdownMenuItem>
                                                )}
                                                <DropdownMenuItem
                                                    data-tracking-id="delete-prebuild"
                                                    onClick={() => {
                                                        if (projectId && pb.id) {
                                                            deletePrebuild.mutate({
                                                                prebuildId: pb.id,
                                                                projectId,
                                                            });
                                                        }
                                                    }}
                                                    variant="destructive"
                                                >
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownActions>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    }

    return (
        <div data-testid="project-details-prebuilds-page" className="flex flex-col gap-3">
            {!project?.prebuildConfiguration?.enabled && (sortedPrebuilds?.length || 0) > 0 && (
                <Banner variant="warning" text="Prebuilds are disabled. Environments won't start from a prebuild." />
            )}
            {runningCount > 0 && (
                <Banner
                    variant="progress"
                    text={`${runningCount} prebuild${runningCount > 1 ? "s" : ""} ${runningCount > 1 ? "are" : "is"} currently running...`}
                />
            )}
            {content}
            {selectedPrebuildId && (
                <PrebuildLogsModal
                    prebuildId={selectedPrebuildId}
                    open={!!selectedPrebuildId}
                    onOpenChange={(open) => !open && setSelectedPrebuildId(null)}
                    onRerun={handleRerun}
                    onCancel={handleCancel}
                />
            )}
        </div>
    );
};

function triggerToText(trigger?: PrebuildMetadata_PrebuildTrigger): string {
    switch (trigger) {
        case PrebuildMetadata_PrebuildTrigger.MANUAL:
            return "Manual";
        case PrebuildMetadata_PrebuildTrigger.SCHEDULED:
            return "Scheduled";
        default:
            return "—";
    }
}

function statusInfo(
    phase?: PrebuildPhase,
    desiredPhase?: PrebuildPhase,
): {
    label: string;
    icon: "spinner" | "success" | "error" | "cancelled";
} {
    // Check if prebuild is in a finished state
    const finished =
        phase === PrebuildPhase.COMPLETED ||
        phase === PrebuildPhase.FAILED ||
        phase === PrebuildPhase.CANCELLED ||
        phase === PrebuildPhase.DELETED;

    // Optimistically show cancelling/deleting state when desired phase is set but not yet reached
    if (desiredPhase === PrebuildPhase.CANCELLED && !finished) {
        return { label: "Cancelling", icon: "spinner" };
    }
    if (desiredPhase === PrebuildPhase.DELETED && phase !== PrebuildPhase.DELETED) {
        return { label: "Deleting", icon: "spinner" };
    }

    switch (phase) {
        case PrebuildPhase.PENDING:
        case PrebuildPhase.STARTING:
            return { label: "Starting", icon: "spinner" };
        case PrebuildPhase.RUNNING:
            return { label: "Running", icon: "spinner" };
        case PrebuildPhase.STOPPING:
            return { label: "Stopping", icon: "spinner" };
        case PrebuildPhase.SNAPSHOTTING:
            return { label: "Snapshotting", icon: "spinner" };
        case PrebuildPhase.COMPLETED:
            return { label: "Succeeded", icon: "success" };
        case PrebuildPhase.FAILED:
            return { label: "Failed", icon: "error" };
        case PrebuildPhase.CANCELLING:
            return { label: "Cancelling", icon: "spinner" };
        case PrebuildPhase.CANCELLED:
            return { label: "Cancelled", icon: "cancelled" };
        case PrebuildPhase.DELETING:
            return { label: "Deleting", icon: "spinner" };
        case PrebuildPhase.DELETED:
            return { label: "Deleted", icon: "spinner" };
        default:
            return { label: "—", icon: "spinner" };
    }
}
